import React from 'react';
import { View, Pressable, ScrollView } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useHospitalityStore } from '../store/useHospitalityStore';
import { inr } from '../data/format';
import { useTheme, TONE } from '../theme';
import { Card, CardHead, CardSub, CardBody } from '../components/Card';
import { Row, Col, Spread, Grow } from '../components/Layout';
import Chip from '../components/Chip';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import Button, { buttonFg } from '../components/Button';
import Divider from '../components/Divider';
import Txt from '../components/Txt';
import { Input } from '../components/Input';
import { createOrder, addOrderItem, removeOrderItem, changeOrderItemQty, declineOrderItem, getMenu, type MenuItemDTO } from '../services/orders/order';
import { generateBill, issueBill } from '../services/billing/billing';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';
import { openOrderRows } from '../data/derive';

/**
 * Orders & Upsell — ported from SK Hospitality.dc.html template lines 429-580
 * and the ordersVals() view-model (lines 1548-1593). Deterministic upsell swaps the
 * base line to the premium item. Subtotal/GST/Total are real SK Billing numbers
 * (order.subtotal/taxTotal/grandTotal, set by applyBillingTotals after every
 * create/add-item/bill call) — never computed locally.
 */

// Serializes the "first item creates the real document" step per local stub
// order id. Without this, clicking multiple menu items in quick succession
// (before the first createOrder round-trip resolves) each independently sees
// "no real document yet" and calls createOrder for itself — spinning up N
// separate billing documents instead of one document with N lines. Only the
// last one to resolve ever gets linked via replaceOrderId, silently losing
// every earlier item's charge. Concurrent callers instead await the SAME
// in-flight creation, then add their own item onto the resulting real order.
// Left in the map after a successful creation (harmless — the temp id is
// never reused); cleared on failure so a retry can create fresh.
const orderCreationLocks = new Map<string, Promise<any>>();

const STAT = {
  open: { cls: 'warn' as const, label: 'Open' },
  bill_sent: { cls: 'success' as const, label: 'Bill sent' },
  seated: { cls: 'neutral' as const, label: 'No order yet' },
};

export default function Orders() {
  const t = useTheme();
  const s = useHospitalityStore();
  const { orders, tables, activeOrderId, menuQuery, menuCat } = s;
  const openRows = openOrderRows(tables, orders);
  // Never default to just "the first order in the array" — that array now
  // also holds finished history rows, and blindly picking orders[0] meant a
  // finished bill from an untracked table could silently become the "active"
  // order, showing a live, editable menu/bill for something already settled.
  // Only fall back to a genuinely LIVE row (a real seated table's order);
  // an explicit click on a finished row still opens it (activeOrderId match).
  const liveOrderId = openRows.find((r) => !r.finished && r.orderId)?.orderId;
  const o = orders.find((x) => x.id === activeOrderId) ?? orders.find((x) => x.id === liveOrderId);

  // The "Open orders" list — shown regardless of whether a live order exists,
  // so recently-finished bills stay visible/clickable even when nobody's
  // currently seated or waiting.
  const openOrdersCard = (
    <Card style={{ flex: 1, minWidth: 220, maxWidth: 300 }}>
      <CardHead>
        <Row gap={8}>
          <Txt weight="semibold">Open orders</Txt>
          <Chip variant="warn">{String(openRows.length)}</Chip>
        </Row>
      </CardHead>
      <ScrollView style={{ flex: 1, minHeight: 0 }} contentContainerStyle={{ padding: 16 }}>
        <Col gap={8}>
          {openRows.map((x) => {
            const st = STAT[x.status];
            const active = !!x.orderId && x.orderId === activeOrderId;
            return (
              <Pressable
                key={x.key}
                onPress={() =>
                  x.orderId ? s.setActiveOrder(x.orderId) : s.findOrCreateOrderForTable(x.tableId, x.tableLabel, x.guests ?? undefined)
                }
                style={{
                  width: '100%',
                  borderRadius: t.radii[4],
                  paddingVertical: 9,
                  paddingHorizontal: 12,
                  borderWidth: 1,
                  borderColor: active ? t.colors.accent : t.colors.line,
                  backgroundColor: active ? t.colors.accentSoft : t.colors.card,
                  opacity: x.finished ? 0.7 : 1,
                }}
              >
                <Spread style={{ marginBottom: 4 }}>
                  <Row gap={7}>
                    <Txt mono weight="bold" style={{ fontSize: 13 }}>{x.tableLabel}</Txt>
                    {x.hasUpsell && (
                      <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: TONE.orange }} />
                    )}
                  </Row>
                  <Chip variant={x.finished ? 'default' : st.cls}>{x.finished ? 'Finished' : st.label}</Chip>
                </Spread>
                <Txt size="xs" color="ink3">{x.guests ?? '—'} • {x.items} items · {x.server}</Txt>
                <Txt mono weight="semibold" style={{ fontSize: 14, marginTop: 4 }}>{inr(x.grandTotal)}</Txt>
              </Pressable>
            );
          })}
        </Col>
      </ScrollView>
    </Card>
  );

  // Real SK Catalog menu — fetched once on mount (hooks must run before the
  // early return below, so this stays unconditional regardless of whether an
  // order is open yet).
  const [menu, setMenu] = React.useState<MenuItemDTO[]>([]);
  React.useEffect(() => {
    getMenu().then(setMenu).catch(() => undefined);
  }, []);
  // Real Section names (Category entities) — `category` is a deprecated
  // legacy ENUM field the current menu-authoring flow never populates, so
  // building the chip list from it always collapsed to just "All".
  const menuCats = ['All', ...Array.from(new Set(menu.flatMap((m) => m.sections ?? [])))] as string[];

  // Poll the active order's real bill — without this, a second device that
  // added/removed an item leaves THIS screen showing a stale total until a
  // manual reload (same class of bug as Floor's table-status staleness).
  // Skipped for a local stub (o.id.startsWith('ord_')) — no real document to
  // poll yet.
  React.useEffect(() => {
    if (!o || o.id.startsWith('ord_')) return;
    const orderId = o.id;
    let cancelled = false;
    const id = setInterval(() => {
      generateBill(orderId)
        .then((res) => { if (!cancelled) useHospitalityStore.getState().applyBillingDoc(orderId, res); })
        .catch(() => undefined); // transient fetch errors just skip this tick
    }, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [o?.id]);

  // No LIVE order right now (nobody currently seated/building one) — still
  // show the Open orders list (finished history stays visible/clickable),
  // but never render a menu/bill builder with nothing genuinely active
  // behind it. Clicking any row above sets activeOrderId and re-renders
  // into the full 3-column layout below.
  if (!o) {
    return (
      <View style={{ flex: 1, flexDirection: 'row', gap: 16, padding: 20, minHeight: 0 }}>
        {openOrdersCard}
        <View style={{ flex: 2.4, minWidth: 400, alignItems: 'center', justifyContent: 'center' }}>
          <Txt color="ink3" size="md">
            {openRows.length > 0 ? 'Select an order to view it, or seat a table to start a new one.' : 'No open orders yet.'}
          </Txt>
        </View>
      </View>
    );
  }

  const q = (menuQuery || '').trim().toLowerCase();
  const cat = menuCat || 'All';
  const inCart: Record<string, number> = Object.fromEntries(o.lines.map((l) => [l.name, l.qty]));
  const menuItems = menu.filter((m) => (cat === 'All' || (m.sections ?? []).includes(cat)) && (!q || m.name.toLowerCase().includes(q)));

  const up = o.upsell;
  const billSent = o.status === 'bill_sent';
  // A table only ever gets freed after settling — so "no table currently
  // points at this order" is this app's real signal for "already settled,"
  // not just "sent." Editing (qty/remove) and adding more items (the menu)
  // must not be offered for a closed order, and its bill must never be
  // re-sent — there's nothing left to send.
  const isFinishedOrder = !tables.some((tb) => tb.orderId === o.id);
  // Real numbers only — undefined until the first item creates the billing
  // document (see applyBillingTotals). A brand-new empty order shows ₹0, not
  // a locally-guessed running total.
  const subtotal = o.subtotal ?? 0;
  const taxTotal = o.taxTotal ?? 0;
  const total = o.grandTotal ?? 0;
  const gstPercent = subtotal > 0 ? Math.round((taxTotal / subtotal) * 100) : 0;

  // sessionRef doubles as the order ref for the prototype (no real server order id yet).
  const sessionRef = o.id;

  // Every billing response (create/add-item/remove/qty/bill) carries the
  // REAL lines + GST-correct totals — resync BOTH onto the order, not just
  // the totals. Billing never merges same-item lines the way the optimistic
  // local update does, so skipping the lines resync here is what let the
  // local (merged) view silently diverge from the real (unmerged) document
  // until a reload exposed the difference.
  const applyBillingDoc = (orderId: string, res: { subtotal?: string; tax_total?: string; grand_total?: string; lines?: { id?: string; description: string; quantity?: string; unit_price?: string }[] }) => {
    useHospitalityStore.getState().applyBillingDoc(orderId, res);
  };

  // Add a menu item to the live order (optimistic local update + backend add).
  // o.id starts as a local stub ('ord_...') until the FIRST item creates the
  // real billing document — createOrder, not addOrderItem, for that one; every
  // item after adopts the real id and uses addOrderItem as before. itemId is
  // the real catalog item id (a UUID) — NOT the dish name; catalog_get_item
  // (called downstream in orders.service.ts) 404s on anything else.
  //
  // Whether it's still a stub is checked via the id itself (o.id.startsWith),
  // not o.lines.length — lines increments synchronously and optimistically on
  // every click, so it's not a reliable signal of "does a real document exist
  // yet". The id only flips once replaceOrderId actually lands.
  const handleAddItem = async (itemId: string, name: string, price: number) => {
    const prevLines = o.lines;
    let targetId = o.id;
    // The table whose active_order_id should point at whatever real document
    // id this call ends up with — resolved once up front since o.id itself
    // may change (temp stub → real id) partway through this call.
    const tableId = useHospitalityStore.getState().tables.find((t) => t.orderId === o.id)?.id;
    s.addItem(name, price);
    try {
      if (o.id.startsWith('ord_')) {
        let creation = orderCreationLocks.get(o.id);
        if (!creation) {
          // Stable key (not random) as defense-in-depth alongside the lock
          // above: same local stub id → same key → billing itself would
          // dedupe a genuine retry (e.g. a network resend) even if two
          // requests somehow slipped past the lock.
          creation = createOrder({ sessionRef, items: [{ itemId, quantity: 1 }], idempotencyKey: `create-order:${o.id}`, tableId, tableLabel: o.table, guests: o.guests ?? undefined }).catch((err) => {
            orderCreationLocks.delete(o.id); // let a later click retry cleanly
            throw err;
          });
          orderCreationLocks.set(o.id, creation);
          const res = await creation;
          targetId = res.id;
          useHospitalityStore.getState().replaceOrderId(o.id, res.id);
          applyBillingDoc(res.id, res);
          showSuccess(res, 'Order started');
        } else {
          // Another click already created the real document — wait for it,
          // then add THIS item onto it for real instead of creating a second
          // throwaway document.
          const res = await creation;
          targetId = res.id;
          const addRes = await addOrderItem(res.id, { sessionRef, itemId, quantity: 1, tableId, tableLabel: o.table, guests: o.guests ?? undefined });
          applyBillingDoc(res.id, addRes);
          showSuccess(addRes, 'Added to order');
        }
      } else {
        const res = await addOrderItem(o.id, { sessionRef, itemId, quantity: 1, tableId, tableLabel: o.table, guests: o.guests ?? undefined });
        targetId = res.id;
        // addOrderItem supersedes to a NEW document id — this was the one
        // mutation path in this file that never adopted it, leaving the
        // local order pointed at the now-frozen old document. The next poll
        // tick (or the next add) would then silently revert/drop changes.
        useHospitalityStore.getState().replaceOrderId(o.id, res.id);
        applyBillingDoc(res.id, res);
        showSuccess(res, 'Added to order');
      }
    } catch (err) {
      handleApiError(err, 'Add item failed');
      useHospitalityStore.setState((s) => ({
        orders: s.orders.map((ord) => (ord.id === targetId ? { ...ord, lines: prevLines } : ord)),
      }));
    }
  };

  // Remove a line from the live order. If no real billing document exists
  // yet (o.id is still a local 'ord_...' stub — nothing to delete on the
  // backend), this is local-only; the first add-item creates the document
  // from whatever's left in the store at that point.
  const handleRemoveLine = async (lineId: string, name: string) => {
    const prevLines = o.lines;
    const orderId = o.id;
    s.removeLine(lineId);
    if (orderId.startsWith('ord_')) return;
    const tableId = useHospitalityStore.getState().tables.find((t) => t.orderId === orderId)?.id;
    try {
      // Target by description, not lineId — billing mints a fresh line id
      // on every supersede, so an id captured here can already be stale by
      // the time this request lands (e.g. a sibling add/remove/qty call
      // resolved first). Description survives across supersedes as long as
      // the item is still on the order.
      const res = await removeOrderItem(orderId, { description: name, idempotencyKey: `remove:${orderId}:${name}`, tableId, tableLabel: o.table, guests: o.guests ?? undefined });
      if (res.deleted) {
        // That was the last line — the backend deleted the (still-DRAFT)
        // document rather than superseding into an empty one. Fall back to
        // a fresh local stub, same shape as a brand-new order, so the next
        // add-item creates a new document instead of hitting the now-gone one.
        const freshId = 'ord_' + Date.now();
        useHospitalityStore.getState().replaceOrderId(orderId, freshId);
        useHospitalityStore.getState().setOrderTotals(freshId, { subtotal: 0, taxTotal: 0, grandTotal: 0 });
      } else {
        // removeOrderItem supersedes to a NEW document id, same as add-item.
        useHospitalityStore.getState().replaceOrderId(orderId, res.id);
        applyBillingDoc(res.id, res);
      }
    } catch (err) {
      handleApiError(err, 'Remove item failed');
      useHospitalityStore.setState((s) => ({
        orders: s.orders.map((ord) => (ord.id === orderId ? { ...ord, lines: prevLines } : ord)),
      }));
    }
  };

  // Change a line's quantity. Same supersede pattern as remove — the new
  // absolute quantity (not a delta) is what's sent, clamped to >= 1 (drop to
  // 0 is what the × button is for, not the stepper).
  const handleChangeQty = async (lineId: string, name: string, delta: number) => {
    const prevLines = o.lines;
    const orderId = o.id;
    const line = o.lines.find((l) => l.id === lineId);
    const quantity = Math.max(1, (line?.qty ?? 1) + delta);
    s.changeQty(lineId, delta);
    if (orderId.startsWith('ord_')) return;
    const tableId = useHospitalityStore.getState().tables.find((t) => t.orderId === orderId)?.id;
    try {
      // Target by description — see handleRemoveLine for why not lineId.
      const res = await changeOrderItemQty(orderId, {
        description: name,
        quantity,
        idempotencyKey: `qty:${orderId}:${name}:${quantity}`,
        tableId,
        tableLabel: o.table,
        guests: o.guests ?? undefined,
      });
      useHospitalityStore.getState().replaceOrderId(orderId, res.id);
      applyBillingDoc(res.id, res);
    } catch (err) {
      handleApiError(err, 'Update quantity failed');
      useHospitalityStore.setState((s) => ({
        orders: s.orders.map((ord) => (ord.id === orderId ? { ...ord, lines: prevLines } : ord)),
      }));
    }
  };

  // Accept the VOICE upsell — swaps the base line locally and records the accept upstream.
  const handleAcceptUpsell = async () => {
    const tableId = useHospitalityStore.getState().tables.find((t) => t.orderId === o.id)?.id;
    s.acceptUpsell();
    if (!up) return;
    try {
      const res = await addOrderItem(o.id, {
        sessionRef,
        itemId: up.toItem,
        quantity: 1,
        source: 'upsell',
        suggestionId: up.ruleId,
        tableId,
        tableLabel: o.table,
        guests: o.guests ?? undefined,
      });
      // addOrderItem supersedes to a NEW document id (no in-place add-line) —
      // res.id is that new id, not o.id. Previously this updated totals on
      // the now-stale o.id and never adopted the new one at all.
      useHospitalityStore.getState().replaceOrderId(o.id, res.id);
      applyBillingDoc(res.id, res);
      showSuccess(res, 'Upsell added');
    } catch (err) {
      handleApiError(err, 'Upsell failed');
    }
  };

  // Decline the upsell — keeps the base line locally and records the decline upstream.
  const handleDeclineUpsell = async () => {
    s.declineUpsell();
    if (!up) return;
    try {
      const res = await declineOrderItem(o.id, {
        sessionRef,
        itemId: up.fromItem,
        suggestionId: up.ruleId,
      });
      showSuccess(res, 'Upsell declined');
    } catch (err) {
      handleApiError(err, 'Decline failed');
    }
  };

  // Send the bill for this order — actually issues the real document
  // (DRAFT → ISSUED), not just a re-read, so "Bill sent" survives a reload.
  const handleSendBill = async () => {
    s.sendBill();
    try {
      const res = await issueBill(o.id);
      applyBillingDoc(o.id, res);
      showSuccess(res, 'Bill sent');
    } catch (err) {
      handleApiError(err, 'Bill failed');
    }
  };

  return (
    // No page-level scroll. Only the sections that genuinely need it scroll
    // internally (open orders, current-order lines, the menu grid) within a
    // fixed-height row — the bill stays permanently visible in the right
    // column, which has no scroll of its own at all.
    <View style={{ flex: 1, flexDirection: 'row', gap: 16, padding: 20, minHeight: 0 }}>
      {/* left column: open orders list — flex-ratio, not a fixed px width, so
          the menu column (the one that actually needs room for a 2-col grid)
          doesn't get squeezed on laptop-width screens. */}
      {openOrdersCard}

      {/* middle column: ONE scroll container for the whole column — current
          order flows naturally into the menu below it, exactly like scrolling
          a single page. No nested scrollbars: neither card has its own. */}
      <ScrollView style={{ flex: 2.4, minWidth: 400 }} contentContainerStyle={{ gap: 16 }}>

      {/* current order — full height, no internal scroll; it's just the top
          of the one shared scroll area. */}
      <Card>
        <CardHead>
          <Row gap={10}>
            <View
              style={{
                width: 32, height: 32, borderRadius: t.radii[3], alignItems: 'center', justifyContent: 'center',
                backgroundColor: TONE.orange + '26', borderWidth: 1.5, borderColor: TONE.orange,
              }}
            >
              <Txt mono weight="semibold" size="sm" color={TONE.orange}>{o.table}</Txt>
            </View>
            <Col gap={1}>
              <Txt weight="semibold">{o.guests ?? 'Unknown'} guests</Txt>
              <CardSub>Server {o.server || '—'} · {isFinishedOrder ? 'closed order' : 'live order'}</CardSub>
            </Col>
          </Row>
          <Chip variant={STAT[o.status].cls} dot>{STAT[o.status].label}</Chip>
        </CardHead>
        <CardBody style={{ paddingTop: 0 }}>
          <Col gap={0}>
            {o.lines.map((l) => (
              <Row
                key={l.id}
                gap={10}
                style={{ paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: t.colors.line, alignItems: 'center' }}
              >
                <Grow>
                  <Row gap={7}>
                    <Txt weight="medium" style={{ fontSize: 13, lineHeight: 17 }}>{l.name}</Txt>
                    {l.premium && <Chip variant="accent">premium</Chip>}
                  </Row>
                  <Txt mono size="xs" color="ink3" style={{ marginTop: 2 }}>{inr(l.price)} each</Txt>
                </Grow>
                {/* A closed order is read-only — no qty stepper, no remove
                    button, just the final quantity as plain text. */}
                {isFinishedOrder ? (
                  <Txt mono size="sm" weight="medium" style={{ minWidth: 26, textAlign: 'center' }}>{String(l.qty)}</Txt>
                ) : (
                  // One compact pill (shared border, internal dividers) instead
                  // of three separate floating buttons.
                  <Row
                    gap={0}
                    style={{
                      borderWidth: 1, borderColor: t.colors.line, borderRadius: t.radii[3],
                      overflow: 'hidden', alignItems: 'stretch',
                    }}
                  >
                    <Pressable
                      onPress={() => { void handleChangeQty(l.id, l.name, -1); }}
                      style={{ width: 26, alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Icon name="minus" size={11} strokeWidth={2.2} color={t.colors.ink2} />
                    </Pressable>
                    <View style={{ width: 1, backgroundColor: t.colors.line }} />
                    <View style={{ minWidth: 26, alignItems: 'center', justifyContent: 'center' }}>
                      <Txt mono size="sm" weight="medium">{String(l.qty)}</Txt>
                    </View>
                    <View style={{ width: 1, backgroundColor: t.colors.line }} />
                    <Pressable
                      onPress={() => { void handleChangeQty(l.id, l.name, 1); }}
                      style={{ width: 26, alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Icon name="plus" size={11} strokeWidth={2.2} color={t.colors.ink2} />
                    </Pressable>
                  </Row>
                )}
                <Txt mono weight="semibold" style={{ minWidth: 56, textAlign: 'right', fontSize: 13 }}>{inr(l.qty * l.price)}</Txt>
                {!isFinishedOrder && (
                  <Pressable onPress={() => { void handleRemoveLine(l.id, l.name); }} style={{ padding: 4 }}>
                    <Icon name="x" size={13} strokeWidth={1.7} color={t.colors.ink4} />
                  </Pressable>
                )}
              </Row>
            ))}
          </Col>
        </CardBody>
      </Card>

      {/* searchable menu — flows directly beneath the current order in the
          SAME scroll. Search + category filters are sticky within this one
          scroll area, so they stay reachable while browsing dishes. Hidden
          entirely for a closed order — there's nothing left to add. */}
      {!isFinishedOrder && (
      <Card>
        <CardHead>
          <Col gap={2}>
            <Txt weight="semibold">Menu</Txt>
            <CardSub>SK Catalog · search or browse, tap to add</CardSub>
          </Col>
        </CardHead>
        {/* Sticky within the ONE shared scroll (not its own scroll area) —
            stays reachable while the grid below scrolls underneath it. Needs
            an opaque background + its own bottom border since items now
            scroll behind it once it sticks. */}
        <View
          style={{
            position: 'sticky', top: 0, zIndex: 1,
            backgroundColor: t.colors.card,
            paddingHorizontal: 16, paddingTop: 16, paddingBottom: 12, gap: 12,
            borderBottomWidth: 1, borderBottomColor: t.colors.line,
          } as any}
        >
            {/* search box with leading glyph */}
            <View style={{ position: 'relative' }}>
              <Input
                style={{ paddingLeft: 34 }}
                placeholder="Search dishes — fish, biryani, naan…"
                value={menuQuery}
                onChangeText={(v) => s.setMenuQuery(v)}
              />
              <View pointerEvents="none" style={{ position: 'absolute', left: 11, top: 0, bottom: 0, justifyContent: 'center' }}>
                <Svg viewBox="0 0 24 24" width={16} height={16} fill="none">
                  <Path
                    d="M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16zM21 21l-4.3-4.3"
                    stroke={t.colors.ink4}
                    strokeWidth={1.8}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Svg>
              </View>
            </View>

            {/* category filter — horizontally scrollable chip row */}
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6, paddingBottom: 2 }}>
              {menuCats.map((c) => {
                const activeCat = cat === c;
                return (
                  <Pressable
                    key={c}
                    onPress={() => s.setMenuCat(c)}
                    style={{
                      paddingVertical: 5, paddingHorizontal: 11, borderRadius: 8, borderWidth: 1,
                      borderColor: activeCat ? t.colors.accent : t.colors.line,
                      backgroundColor: activeCat ? t.colors.accentSoft : t.colors.card,
                    }}
                  >
                    <Txt size="xs" weight="medium" color={activeCat ? 'accentInk' : 'ink3'}>{c}</Txt>
                  </Pressable>
                );
              })}
            </ScrollView>
        </View>

        {/* menu items — two-column wrap, flows in the SAME shared scroll (no
            scrollbar of its own). Dense by design: name + price + (qty if
            already in cart) + a compact add action — no thumbnails, so more
            dishes fit on screen. Row height trimmed ~25% vs. the ticket rows. */}
        <View style={{ padding: 16, paddingTop: 12 }}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {menuItems.map((m) => (
                <Pressable
                  key={m.id}
                  onPress={() => { void handleAddItem(m.id, m.name, m.price); }}
                  style={{
                    flexGrow: 1, flexBasis: '47%',
                    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                    paddingVertical: 7, paddingHorizontal: 10, borderWidth: 1, borderColor: t.colors.line,
                    borderRadius: t.radii[3], backgroundColor: t.colors.card,
                  }}
                >
                  <Grow>
                    <Txt numberOfLines={1} weight="medium" color="ink" style={{ fontSize: 13 }}>{m.name}</Txt>
                    <Txt mono size="xs" color="ink3">{inr(m.price)}</Txt>
                  </Grow>
                  <Row gap={5}>
                    {!!inCart[m.name] && (
                      <Txt mono size="xs" weight="semibold" color="accent">×{inCart[m.name]}</Txt>
                    )}
                    <View style={{ width: 20, height: 20, borderRadius: t.radii[2], backgroundColor: t.colors.accentSoft, alignItems: 'center', justifyContent: 'center' }}>
                      <Icon name="plus" size={12} strokeWidth={2.2} color={t.colors.accent} />
                    </View>
                  </Row>
                </Pressable>
              ))}
              {menuItems.length === 0 && (
                <View style={{ width: '100%', padding: 24, alignItems: 'center' }}>
                  <Txt size="sm" color="ink4">No dishes match “{menuQuery}”.</Txt>
                </View>
              )}
            </View>
        </View>
      </Card>
      )}

      </ScrollView>

      {/* right column: upsell (if any) + bill. No scroll here at all — the
          bill must always stay fully visible/reachable, never scrolled away
          (upsell + bill together are always compact enough to fit). */}
      <View style={{ flex: 1.3, minWidth: 260, maxWidth: 380, gap: 16 }}>

      {/* upsell */}
      {up && (
        <Card style={{ borderColor: t.colors.accentLine }}>
          <CardHead style={{ backgroundColor: t.colors.accentSoft, borderBottomColor: t.colors.accentLine }}>
            <Row gap={9}>
              <View style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: TONE.orange, alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="star" size={15} strokeWidth={1.6} color="#fff" />
              </View>
              <Col gap={2}>
                <Txt weight="semibold" color="accentInk">VOICE suggests an upsell</Txt>
                <CardSub>Deterministic — from the Catalog rule engine</CardSub>
              </Col>
            </Row>
          </CardHead>
          <CardBody>
            <Col gap={12}>
              <Row gap={8} wrap>
                <Chip variant="neutral" dot>{up.ruleName}</Chip>
                <Txt mono size="xs" color="ink4">{up.ruleId}</Txt>
              </Row>
              <Txt size="sm" color="ink3">
                Triggered by: <Txt color="ink2" weight="medium">{up.trigger}</Txt>
              </Txt>
              <Row gap={12} style={{ padding: 14, borderWidth: 1, borderColor: t.colors.line, borderRadius: 10, backgroundColor: t.colors.paper2 }}>
                <Grow>
                  <Txt size="sm" color="ink3">{up.fromItem}</Txt>
                  <Txt mono weight="semibold" color="ink3" style={{ fontSize: 18, textDecorationLine: 'line-through' }}>{inr(up.fromPrice)}</Txt>
                </Grow>
                <Icon name="arrowr" size={22} strokeWidth={1.8} color={t.colors.accent} />
                <View style={{ flex: 1.3, alignItems: 'flex-end' }}>
                  <Txt size="sm" color="accentInk">{up.toItem}</Txt>
                  <Txt mono weight="bold" color="accent" style={{ fontSize: 30, lineHeight: 33 }}>{inr(up.toPrice)}</Txt>
                </View>
              </Row>
              {!o.accepted && !o.declined && (
                <Row gap={8}>
                  <Button variant="accent" block style={{ flex: 1 }} onPress={() => handleAcceptUpsell()}>
                    <Icon name="check" size={15} strokeWidth={1.8} color={buttonFg('accent', t.colors)} />
                    <Txt size="md" weight="medium" color={buttonFg('accent', t.colors)}>Accept · +{inr(up.toPrice - up.fromPrice)}</Txt>
                  </Button>
                  <Button variant="default" onPress={() => handleDeclineUpsell()}>Not now</Button>
                </Row>
              )}
              {o.accepted && (
                <Alert variant="success">
                  <Txt size="sm" color="success">
                    Applied — SK Billing recomputed the bill. <Txt size="sm" weight="bold" color="success">+{inr(up.toPrice - up.fromPrice)}</Txt> added.
                  </Txt>
                </Alert>
              )}
              {o.declined && !o.accepted && (
                <Alert>
                  <Txt size="sm" color="ink2">Upsell declined. The rule stays available if the guest changes their mind.</Txt>
                </Alert>
              )}
            </Col>
          </CardBody>
        </Card>
      )}

      {/* bill */}
      <Card>
        <CardHead>
          <Col gap={2}>
            <Txt weight="semibold">Bill</Txt>
            <CardSub>SK Billing · GST-correct</CardSub>
          </Col>
        </CardHead>
        <CardBody>
          <Col gap={12}>
            <Spread>
              <Txt color="ink3">Subtotal</Txt>
              <Txt mono>{inr(subtotal)}</Txt>
            </Spread>
            <Spread>
              <Txt color="ink3">GST{gstPercent > 0 ? ` (${gstPercent}%)` : ''}</Txt>
              <Txt mono>{inr(taxTotal)}</Txt>
            </Spread>
            <Divider />
            <Spread>
              <Txt weight="semibold">Total</Txt>
              <Txt mono weight="bold" style={{ fontSize: 24 }}>{inr(total)}</Txt>
            </Spread>
            <Button variant="accent" size="lg" block disabled={isFinishedOrder} onPress={() => handleSendBill()}>
              <Icon name="receipt" size={16} strokeWidth={1.7} color={buttonFg('accent', t.colors)} />
              <Txt size="lg" weight="medium" color={buttonFg('accent', t.colors)}>
                {billSent ? 'Bill already sent' : `Send bill to ${o.table}`}
              </Txt>
            </Button>
          </Col>
        </CardBody>
      </Card>
      </View>
    </View>
  );
}
